import { useEffect, useState } from 'react';
import { UserDocument } from '@/entities/user-document';
import { getSignedDocumentDownloadUrl } from '@/use-cases/get-signed-document-download-url';
import { addDocumentSigners, NewSigner } from '@/use-cases/add-document-signers';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/toast/toast';
import notifyHB from '@/config/notifyHB';
import DocumentAnnotator from '@/pages/upload-document/document-annotator/document-annotator';

type AddSignerDialogProps = {
	document: UserDocument | null;
	onClose: () => void;
	onSignersAdded: () => void;
};

export const AddSignerDialog = (props: AddSignerDialogProps) => {
	const [isLoadingDocument, setIsLoadingDocument] = useState(false);
	const [signedFile, setSignedFile] = useState<File | null>(null);

	useEffect(() => {
		if (!props.document) {
			setSignedFile(null);
			return;
		}

		const document = props.document;
		setIsLoadingDocument(true);
		const signedPath = `${document.Owner}/${document.Serial}/signed/${document.Path}`;

		getSignedDocumentDownloadUrl(signedPath)
			.then(async (url) => {
				if (!url) throw new Error('No signed document download url');
				const response = await fetch(url);
				const blob = await response.blob();
				const file = new File([blob], document.Path, {
					type: 'application/pdf',
				});
				setSignedFile(file);
			})
			.catch((error) => {
				console.debug(error);
				notifyHB(
					'add-signer-dialog',
					'AddSignerDialog',
					`Failed to load signed document with ${error}`,
				);
				toast({
					mode: 'destructive',
					description:
						'No fue posible cargar el documento firmado',
				});
				props.onClose();
			})
			.finally(() => setIsLoadingDocument(false));
	}, [props.document]);

	const handleAddSigners = async (signers: NewSigner[]) => {
		if (!props.document) return;
		const response = await addDocumentSigners(
			props.document.Serial,
			signers,
		);
		if (response?.success) {
			toast({
				mode: 'default',
				description: 'Se envió la solicitud de firma',
			});
			props.onSignersAdded();
			props.onClose();
		} else {
			toast({
				mode: 'destructive',
				description:
					response?.errorMessage ??
					'No se pudo enviar la solicitud de firma',
			});
			throw new Error(response?.errorMessage ?? 'Failed to add signers');
		}
	};

	return (
		<Dialog
			open={props.document !== null}
			onOpenChange={(open) => (open ? {} : props.onClose())}
		>
			<DialogContent
				aria-describedby={undefined}
				className="overflow-hidden flex flex-col gap-6"
				style={{
					width: '90vw',
					maxWidth: '90vw',
					height: '85vh',
					padding: 32,
				}}
			>
				<DialogTitle>Agregar firmante</DialogTitle>
				<div className="flex-1 min-h-0 overflow-y-auto">
					{!props.document || isLoadingDocument || !signedFile ? (
						<span>Cargando documento...</span>
					) : (
						<DocumentAnnotator
							mode="add-signer"
							documentUlid={props.document.Serial}
							documentToSign={signedFile}
							onDiscardDocument={props.onClose}
							onAddSigners={handleAddSigners}
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
